/**
 * Detalhe da ordem de expedição (Task 14, brief 3.7).
 *
 * Reúne, para a MESMA ordem, a conferência/embalagem/despacho, o registro de
 * ocorrências e o passo de logística reversa, além da visão unificada do
 * estado interno com o rastreio do transportador (D-14.4).
 *
 * Regras que a tela respeita:
 * - **pedido navegável**: a ordem referencia o pedido unificado; não é uma
 *   segunda cópia da venda;
 * - **estado interno + externo lado a lado**: a etapa da expedição e o estado
 *   do transportador aparecem juntos, com a divergência declarada quando há;
 * - **nada inventado**: campo ainda não informado aparece como "não informado"
 *   com o motivo, não como vazio;
 * - **a máquina decide o que é possível**: os próximos passos vêm da regra
 *   pura, não de uma lista de botões desta tela.
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide por bloco (`SectionTitle`), no
 * mesmo padrão visual de `PedidoDetalhe.tsx`/`KpiCard.tsx` (ícone em caixa
 * colorida ao lado do título). Esta tela não tinha NENHUM ícone antes da
 * migração (lacuna real, achado da investigação inicial do cluster) — os
 * quatro ícones abaixo são puramente decorativos (aria-hidden); o nome
 * acessível de cada bloco continua sendo o texto do `h3`. Nenhum `data-*`/
 * aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "../ui/index.js";
import { ArrowLeft, Clock, Signpost, TriangleAlert, Truck, type LucideIcon } from "lucide-react";
import { FulfillmentBoundary } from "../components/FulfillmentBoundary.js";
import { FulfillmentStateMachine } from "../components/FulfillmentStateMachine.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { PendingField } from "../components/PendingField.js";
import {
  fulfillmentTaskById,
  isFulfillmentSourceAwaitingContract,
  orderDrillDown,
  unifiedView
} from "../fulfillment/dataset.js";
import { FULFILLMENT_STAGE_LABELS, OCCURRENCE_KIND_LABELS } from "../fulfillment/state-machine.js";
import { ORDER_ORIGIN_LABELS } from "../orders/state-machine.js";
import { useFulfillmentOccurrence, useFulfillmentStage } from "../queries.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatDateTime } from "../format.js";
import type { FulfillmentDataset } from "../fulfillment/dataset.js";
import type { ExpedicaoDetalheViewProps, OccurrenceHandle, StageTransitionHandle } from "../fulfillment/view-props.js";
import type { FulfillmentTask } from "../api/types.js";

const TRACKING_STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmado (aguardando postagem)",
  shipped: "Postado",
  in_transit: "Em trânsito",
  delivered: "Entregue",
  exception: "Exceção reportada"
};

function pending(label: string): ReactElement {
  return <PendingField label={label} />;
}

/** Casca de seção no padrão aprovado: cartão tokenizado + título h3. */
const BLOCK_CLASS = "rounded-panel border border-border bg-surface p-4 shadow-s1 flex flex-col gap-3";

/**
 * Título de bloco com ícone em caixa colorida — mesmo padrão visual do
 * `kpi-card__icon` em `KpiCard.tsx` (ícone decorativo, o nome acessível do
 * bloco continua sendo o texto do `h3`).
 */
function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-wash text-brand-text">
        <Icon className="h-4 w-4" aria-hidden focusable={false} />
      </span>
      <h3 className="text-lg font-extrabold text-ink">{children}</h3>
    </div>
  );
}

function OrderBlock({ task }: { task: FulfillmentTask }): ReactElement {
  const drill = orderDrillDown(task);
  return (
    <section className={`fulfillment-detail__order ${BLOCK_CLASS}`} data-section="pedido">
      <SectionTitle icon={Signpost}>Pedido</SectionTitle>
      <p data-order-origin={task.orderOrigin}>
        {`Canal: ${ORDER_ORIGIN_LABELS[task.orderOrigin]} · identificador no canal: `}
        <span data-order-external-id={task.orderExternalId}>{task.orderExternalId}</span>
      </p>
      <p data-order-link={drill.route}>
        <Link to={drill.route}>{drill.label}</Link>
      </p>
      {task.customerId ? (
        <p data-customer-id={task.customerId}>
          {"Cliente: "}
          <Link to={`/clientes/${task.customerId}`}>{task.customerName ?? task.customerId}</Link>
        </p>
      ) : (
        <p>
          {"Cliente: "}
          {task.customerName ?? pending("venda sem identidade canônica resolvida")}
        </p>
      )}
    </section>
  );
}

/** Visão unificada: estado interno + estado do transportador, lado a lado. */
function UnifiedBlock({ task }: { task: FulfillmentTask }): ReactElement {
  const view = unifiedView(task);
  return (
    <section className={`fulfillment-detail__unified ${BLOCK_CLASS}`} data-section="visao-unificada">
      <SectionTitle icon={Truck}>Visão unificada (interno + transportador)</SectionTitle>
      <p data-fulfillment-stage={task.stage}>
        {"Etapa interna: "}
        <span>{FULFILLMENT_STAGE_LABELS[task.stage]}</span>
      </p>
      <p data-tracking-status={task.tracking.status ?? ""} data-tracking-source={task.tracking.source}>
        {"Transportador: "}
        {task.tracking.status === null
          ? pending("sem evento do transportador ainda")
          : `${TRACKING_STATUS_LABELS[task.tracking.status] ?? task.tracking.status} — ${task.tracking.note}`}
      </p>
      <p className="page__note text-sm text-muted" data-fulfillment-sync={view.sync}>
        {`Sincronização: ${view.note}`}
      </p>
      <p data-tracking-code={task.trackingCode ?? ""} data-carrier={task.carrier ?? ""}>
        {"Transportadora: "}
        {task.carrier ?? pending("informada no despacho")}
        {" · rastreio: "}
        {task.trackingCode ?? pending("informado no despacho")}
      </p>
    </section>
  );
}

function OccurrenceBlock({ task }: { task: FulfillmentTask }): ReactElement | null {
  if (!task.occurrenceKind) return null;
  return (
    <section className={`fulfillment-detail__occurrence ${BLOCK_CLASS}`} data-section="ocorrencia">
      <SectionTitle icon={TriangleAlert}>Ocorrência pendente</SectionTitle>
      <p data-occurrence-kind={task.occurrenceKind}>
        {`${OCCURRENCE_KIND_LABELS[task.occurrenceKind]}: ${task.occurrenceNote}`}
      </p>
    </section>
  );
}

function TimesBlock({ task }: { task: FulfillmentTask }): ReactElement {
  return (
    <section className={`fulfillment-detail__times ${BLOCK_CLASS}`} data-section="tempos">
      <SectionTitle icon={Clock}>Marcos de tempo</SectionTitle>
      <p data-started-at={task.startedAt ?? ""}>
        {"Início: "}
        {task.startedAt ? formatDateTime(task.startedAt) : pending("a tarefa ainda não foi iniciada")}
      </p>
      <p data-finished-at={task.finishedAt ?? ""}>
        {"Conclusão: "}
        {task.finishedAt ? formatDateTime(task.finishedAt) : pending("ainda em andamento")}
      </p>
    </section>
  );
}

export function ExpedicaoDetalheView({ tasks, taskId, transition, occurrence }: ExpedicaoDetalheViewProps): ReactElement {
  if (isFulfillmentSourceAwaitingContract(tasks)) {
    return (
      <div className="page page--expedicao-detalhe flex flex-col gap-6" data-page="expedicao-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Expedição</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Fila de expedição ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/platform/fulfillment não respondeu neste ambiente, então não há ordem de expedição para abrir."
        />
      </div>
    );
  }
  const task = fulfillmentTaskById(tasks, taskId);
  if (!task) {
    return (
      <div className="page page--expedicao-detalhe flex flex-col gap-6" data-page="expedicao-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Expedição</h2>
        </header>
        <StatusPanel
          kind="empty"
          title={`Expedição ${taskId} não está na fila`}
          detail="A fonte respondeu e não tem este identificador — dizer isso é melhor que mostrar uma tela vazia."
        />
        <Link to="/expedicao" className="w-fit text-sm font-semibold text-brand-text hover:underline">
          Voltar para a fila de expedição
        </Link>
      </div>
    );
  }
  return (
    <div className="page page--expedicao-detalhe flex flex-col gap-6" data-page="expedicao-detalhe">
      <header className="drilldown-header flex flex-col gap-3">
        <Link to="/expedicao" className="drilldown-header__back inline-flex w-fit items-center gap-1 text-sm font-bold text-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden focusable={false} />
          Voltar para a fila de expedição
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">{`Expedição ${task.id}`}</h2>
          <Badge tone="info">{FULFILLMENT_STAGE_LABELS[task.stage]}</Badge>
          <Badge tone="neutral">{ORDER_ORIGIN_LABELS[task.orderOrigin]}</Badge>
          {task.occurrenceKind ? <Badge tone="warning">{OCCURRENCE_KIND_LABELS[task.occurrenceKind]}</Badge> : null}
        </div>
        <p className="drilldown-header__origin m-0 text-sm text-muted" data-record-id={task.id}>
          {`Criada em ${formatDateTime(task.createdAt)} · atualizada em ${formatDateTime(task.updatedAt)}`}
        </p>
      </header>
      {/* Blocos informativos lado a lado; a máquina de estados (ações) ocupa a largura toda. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <OrderBlock task={task} />
        <UnifiedBlock task={task} />
        <OccurrenceBlock task={task} />
        <TimesBlock task={task} />
      </div>
      <FulfillmentStateMachine task={task} transition={transition} occurrence={occurrence} />
    </div>
  );
}

export function ExpedicaoDetalhe(): ReactElement {
  const params = useParams();
  const taskId = params.taskId ?? "";
  const stageMutation = useFulfillmentStage();
  const occurrenceMutation = useFulfillmentOccurrence();
  // Mesmo padrão da transição de pedido (Task 12): a view recebe os caminhos de
  // gravação por prop, então o teste exercita os MESMOS submits sem navegador.
  const transition: StageTransitionHandle = {
    submit: (input) => stageMutation.mutate({ taskId, input }),
    pending: stageMutation.isPending,
    error: stageMutation.error ?? null,
    savedStage: stageMutation.data?.stage ?? null
  };
  const occurrence: OccurrenceHandle = {
    submit: (input) => occurrenceMutation.mutate({ taskId, ...input }),
    pending: occurrenceMutation.isPending,
    error: occurrenceMutation.error ?? null,
    savedOccurrence: occurrenceMutation.data?.occurrenceKind ?? null
  };
  return (
    <FulfillmentBoundary>
      {(tasks: FulfillmentDataset) => (
        <ExpedicaoDetalheView tasks={tasks} taskId={taskId} transition={transition} occurrence={occurrence} />
      )}
    </FulfillmentBoundary>
  );
}
